/**
 * Breadcrumb — Feb 2026 header polish. Derives its trail from the current
 * pathname so no per-page wiring is needed. Renders inline on the top
 * header (desktop only — mobile bottom-nav already anchors context).
 *
 * Route → label map is authored below. Unknown routes fall back to a
 * title-cased segment string.
 */
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Human-readable label per path segment. Keep additions here as new
// routes are added. Kebab / snake segments auto-title-case at render.
const SEGMENT_LABELS = {
    app:              "Dashboard",
    terminal:         "Terminal",
    catalog:          "Catalog",
    options:          "Options",
    bonds:            "Bonds",
    leaderboard:      "Leaderboard",
    "copy-trading":   "Copy Trading",
    community:        "Community",
    subscription:     "Billing",
    settings:         "Settings",
    admin:            "Admin",
    agency:           "Agency",
    profile:          "Profile",
    inbox:            "Inbox",
    obondo:           "Obondo",
    kimani:           "Kimani",
};

function labelFor(seg) {
    if (SEGMENT_LABELS[seg]) return SEGMENT_LABELS[seg];
    return seg
        .split(/[-_]/)
        .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
        .join(" ");
}

export default function Breadcrumb() {
    const { pathname } = useLocation();
    const { user }     = useAuth();
    const isAdmin      = user?.role === "admin";

    // Strip leading/trailing slashes and split into segments.
    const segs = pathname.split("/").filter(Boolean);
    if (segs.length === 0) return null;

    // For super-admins, reframe the trail so it always reads
    // "Super Admin › <child>" — reinforces that they're OBSERVING the
    // trader experience, not participating in it.
    //   /app/terminal  → Super Admin › Terminal      (Super Admin → /admin)
    //   /admin         → Super Admin
    //   /admin/foo     → Super Admin › Foo
    let trail;
    if (isAdmin && segs[0] === "app" && segs.length > 1) {
        // Replace the leading "app" (Dashboard) segment with a synthetic
        // Super Admin entry pointing back to /admin.
        const rest = segs.slice(1).map((seg, i) => ({
            seg,
            label: labelFor(seg),
            href:  "/app/" + segs.slice(1, i + 2).join("/"),
            last:  i === segs.length - 2,
        }));
        trail = [
            { seg: "admin", label: "Super Admin", href: "/admin", last: false },
            ...rest,
        ];
    } else {
        trail = segs.map((seg, i) => ({
            seg,
            label: (isAdmin && seg === "admin") ? "Super Admin" : labelFor(seg),
            href:  "/" + segs.slice(0, i + 1).join("/"),
            last:  i === segs.length - 1,
        }));
    }

    const homeHref = isAdmin ? "/admin" : "/app";

    return (
        <nav
            aria-label="Breadcrumb"
            data-testid="header-breadcrumb"
            className="hidden md:flex items-center gap-1 text-xs text-muted-foreground min-w-0"
        >
            <Link
                to={homeHref}
                data-testid="breadcrumb-home"
                className="flex items-center hover:text-foreground transition-colors shrink-0"
                aria-label="Home"
            >
                <Home className="w-3.5 h-3.5" />
            </Link>
            {trail.map((t) => (
                <span key={t.href} className="flex items-center gap-1 min-w-0">
                    <ChevronRight className="w-3 h-3 shrink-0 opacity-60" />
                    {t.last ? (
                        <span
                            className="font-semibold text-foreground truncate"
                            data-testid={`breadcrumb-current-${t.seg}`}
                        >
                            {t.label}
                        </span>
                    ) : (
                        <Link
                            to={t.href}
                            data-testid={`breadcrumb-link-${t.seg}`}
                            className="hover:text-foreground transition-colors truncate"
                        >
                            {t.label}
                        </Link>
                    )}
                </span>
            ))}
        </nav>
    );
}
